import { useQuery } from '@tanstack/react-query';
import {
  TANSTACK_KIOSK_DATA_KEY,
  TANSTACK_OWNED_KIOSK_KEY,
  TANSTACK_KIOSK_KEY,
} from '../utils/constants';
import { useSuiClient } from '@mysten/dapp-kit';
import { SuiObjectResponse } from '@mysten/sui.js/client';
import {
  Kiosk,
  KioskData,
  KioskItem,
  KioskOwnerCap,
  getKioskObject,
} from '@mysten/kiosk';
import {
  isSudoObject,
  parseObjectDisplays,
} from '../utils/utils';
import { OwnedObjectType } from '../app/components/Inventory/OwnedObjects';
import { useKioskClient } from '../context/KioskClientContext';
import { useEssential } from '../app/App';

export type KioskFnType = (item: OwnedObjectType) => Promise<void> | void;

/**
 * A helper to get user's kiosks.
 * If the user doesn't have a kiosk, the return is an object with null values.
 */
export function useOwnedKiosk(address: string | undefined) {
  const kioskClient = useKioskClient();

  return useQuery({
    queryKey: [TANSTACK_OWNED_KIOSK_KEY, address],
    refetchOnMount: false,
    retry: false,
    queryFn: async (): Promise<{
      caps: KioskOwnerCap[];
      kioskId: string | undefined;
      kioskCap: string | undefined;
      kioskOwnerCap: KioskOwnerCap;
    } | null> => {
      if (!address) return null;

      const { kioskOwnerCaps, kioskIds } = await kioskClient.getOwnedKiosks({
        address: address,
      });

      // TODO: Need to find the kiosk that contains Sudo objects
      return {
        caps: kioskOwnerCaps,
        kioskId: kioskIds[0],
        kioskCap: kioskOwnerCaps[0]?.objectId,
        kioskOwnerCap: kioskOwnerCaps[0],
      };
    },
  });
}

/**
 * A hook to fetch a kiosk (items, listings, etc) by its id.
 */
export function useKiosk(kioskId: string | undefined | null) {
  const kioskClient = useKioskClient();
  const { consts } = useEssential();

  return useQuery({
    queryKey: [TANSTACK_KIOSK_KEY, kioskId],
    queryFn: async (): Promise<{
      kioskData: KioskData | null;
      items: SuiObjectResponse[];
    }> => {
      if (!kioskId) return { kioskData: null, items: [] };
      try {
        const res = await kioskClient.getKiosk({
          id: kioskId,
          options: {
            withObjects: true,
          },
        });

        return {
          kioskData: res,
          items: res.items,
        };
      } catch (error) {
        console.log(error);
        return {
          kioskData: null,
          items: [],
        };
      }
    },
    retry: false,
    select: ({
      items,
      kioskData,
    }): {
      items: OwnedObjectType[];
    } => {
      if (!kioskData) return { items: [] };

      const kioskSudoItems = kioskData.items.filter(item =>
        isSudoObject(item, consts),
      );

      // parse the displays for FE.
      const displays = parseObjectDisplays(items) || {};

      // attach the displays to the objects.
      const ownedItems = kioskSudoItems.map((item: KioskItem) => {
        return {
          ...item,
          display: displays[item.objectId] || {},
          fields: {},
          owner: item.data?.owner,
        };
      });

      // return the items & listings.
      return {
        items: ownedItems,
      };
    },
  });
}

/**
 * A hook to fetch a kiosk's details.
 */
export function useKioskDetails(kioskId: string | undefined | null) {
  const provider = useSuiClient();

  return useQuery({
    queryKey: [TANSTACK_KIOSK_DATA_KEY, kioskId],
    queryFn: async (): Promise<Kiosk | null> => {
      if (!kioskId) return null;
      return await getKioskObject(provider, kioskId);
    },
  });
}
